import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';

import BlueButton from 'components/Shared/BlueButton/BlueButton';
import RequestAccessDialog from './RequestAccessDialog';
import LearnMoreDialog, {
  LEARN_MORE_ROWS,
  chip,
  timedChip,
} from './LearnMoreDialog';
import { HeroArt, CrewArt } from './LandingArt';
import {
  requestInspectAccess,
  markInspectLandingSeen,
  readInspectAccessRequested,
} from 'store/actions/inspectLanding';
// The result chips on this page are the Inspect module's own classes; the module may never have been opened.
import 'components/Inspect/style.css';
import './style.css';

// Spent on the page this long counts as seen: the sidebar badge becomes a dot (once per user per session).
var SEEN_DELAY_MS = 3000;
// The pause state of the two animation controls, kept for the session (cleared with the other Inspect keys).
var ANIMATIONS_KEY = 'inspectLandingAnimations';
var GAUGE_TRACK = '226.2 75.4';
var GAUGE_ARC = '185.5 116.1';

// Example chips of the five list rows.
var ROW_EXAMPLES = {
  due: function (t) {
    return [
      timedChip('due-soon', t('inspectLanding:chipDueSoon'), t('inspectLanding:exDueSoonTime')),
      timedChip('overdue', t('inspectLanding:chipOverdue'), t('inspectLanding:exOverdueTime')),
      chip('scheduled', t('inspectLanding:chipScheduled')),
    ];
  },
  results: function (t) {
    return [
      chip('pass', t('inspectLanding:chipPass')),
      chip('fail', t('inspectLanding:chipFail')),
      chip('incomplete', t('inspectLanding:chipIncomplete')),
    ];
  },
  repairs: function (t) {
    return [
      chip('fail', t('inspectLanding:chipFail')),
      <span key="arrow" className="material-symbols-outlined" aria-hidden="true">arrow_forward</span>,
      chip('corrected', t('inspectLanding:chipCorrected')),
    ];
  },
  skipped: function (t) {
    return [
      timedChip('neglected', t('inspectLanding:chipNeglected'), t('inspectLanding:exNeglectedTime')),
    ];
  },
};

function accountUUIDOf(props) {
  return (props.currentAccount && props.currentAccount.uuid) || '';
}

function readPaused() {
  try {
    return sessionStorage.getItem(ANIMATIONS_KEY) === 'paused';
  } catch (e) {
    return false;
  }
}

function writePaused(paused) {
  try {
    if (paused) {
      sessionStorage.setItem(ANIMATIONS_KEY, 'paused');
    } else {
      sessionStorage.removeItem(ANIMATIONS_KEY);
    }
  } catch (e) {
    // storage unavailable: the choice just does not persist
  }
}

function hasObserver() {
  return typeof window !== 'undefined' && !!window.IntersectionObserver;
}

// What Inspect users see while Inspect is off for the stack and the user.
export class InspectLandingPage extends Component {
  constructor(props) {
    super(props);
    var observed = hasObserver();
    this.state = {
      dialogOpen: false,
      openRow: null,
      paused: readPaused(),
      // a figure waits for the viewer to scroll it into view when IntersectionObserver exists
      waiting: { hero: observed, crew: observed },
      requestState: readInspectAccessRequested(accountUUIDOf(props)),
    };
    this._isMounted = false;
    this._seenTimer = null;
    this._observer = null;
    this._figures = { hero: null, crew: null };
    this.openDialog = this.openDialog.bind(this);
    this.closeDialog = this.closeDialog.bind(this);
    this.handleSent = this.handleSent.bind(this);
    this.closeLearnMore = this.closeLearnMore.bind(this);
    this.requestFromLearnMore = this.requestFromLearnMore.bind(this);
    this.togglePause = this.togglePause.bind(this);
    this.handleIntersect = this.handleIntersect.bind(this);
    this.setHeroFigure = this.setHeroFigure.bind(this);
    this.setCrewFigure = this.setCrewFigure.bind(this);
  }

  componentDidMount() {
    this._isMounted = true;
    this.startSeenTimer();
    this.startObserver();
  }

  componentDidUpdate(prevProps) {
    var uuid = accountUUIDOf(this.props);
    if (uuid !== accountUUIDOf(prevProps)) {
      // The gate stays mounted across a stack switch on the same route.
      this.clearSeenTimer();
      this.startSeenTimer();
      this.setState({
        requestState: readInspectAccessRequested(uuid),
        dialogOpen: false,
        openRow: null,
      });
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
    this.clearSeenTimer();
    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }
  }

  startSeenTimer() {
    var user = this.props.currentUser;
    if (user && user.dashboardSettings && user.dashboardSettings.inspectLandingSeen === true) return;
    var self = this;
    this._seenTimer = setTimeout(function () {
      self._seenTimer = null;
      if (self.props.markInspectLandingSeen) self.props.markInspectLandingSeen();
    }, SEEN_DELAY_MS);
  }

  clearSeenTimer() {
    if (this._seenTimer) {
      clearTimeout(this._seenTimer);
      this._seenTimer = null;
    }
  }

  startObserver() {
    if (!hasObserver()) return;
    this._observer = new window.IntersectionObserver(this.handleIntersect, { threshold: 0.2 });
    if (this._figures.hero) this._observer.observe(this._figures.hero);
    if (this._figures.crew) this._observer.observe(this._figures.crew);
  }

  // A figure starts its animation the first time it is in view; once both have started, stop observing.
  handleIntersect(entries) {
    if (!this._isMounted || !this._observer) return;
    var self = this;
    var started = {};
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      ['hero', 'crew'].forEach(function (key) {
        if (self._figures[key] && self._figures[key] === entry.target) {
          started[key] = true;
          if (self._observer.unobserve) self._observer.unobserve(entry.target);
        }
      });
    });
    var keys = Object.keys(started);
    if (keys.length === 0) return;
    this.setState(function (state) {
      var waiting = Object.assign({}, state.waiting);
      keys.forEach(function (key) {
        waiting[key] = false;
      });
      return { waiting: waiting };
    });
  }

  setHeroFigure(el) {
    this._figures.hero = el;
  }

  setCrewFigure(el) {
    this._figures.crew = el;
  }

  togglePause() {
    var paused = !this.state.paused;
    writePaused(paused);
    this.setState({ paused: paused });
  }

  openDialog() {
    this.setState({ dialogOpen: true });
  }

  closeDialog() {
    this.setState({ dialogOpen: false });
  }

  openLearnMore(row) {
    this.setState({ openRow: row });
  }

  closeLearnMore() {
    this.setState({ openRow: null });
  }

  // Request Access inside a Learn More dialog: close it and open the confirm dialog.
  requestFromLearnMore() {
    this.setState({ openRow: null, dialogOpen: true });
  }

  handleSent(status, accountUUID) {
    if (!this._isMounted) return;
    if (accountUUID !== accountUUIDOf(this.props)) return;
    this.setState({ requestState: status });
  }

  renderPause() {
    var t = this.props.t;
    var paused = this.state.paused;
    return (
      <button
        type="button"
        className="inspect-landing-pause"
        aria-pressed={paused ? 'true' : 'false'}
        aria-label={t(paused ? 'inspectLanding:playAnimations' : 'inspectLanding:pauseAnimations')}
        onClick={this.togglePause}
      >
        <span className="material-symbols-outlined" aria-hidden="true">{paused ? 'play_arrow' : 'pause'}</span>
      </button>
    );
  }

  renderGauge(t) {
    return (
      <span className="inspect-landing-gauge">
        <span className="inspect-landing-gauge-d">
          <svg width="56" height="56" viewBox="0 0 120 120" aria-hidden="true">
            <circle
              className="inspect-landing-gauge-track"
              cx="60"
              cy="60"
              r="48"
              fill="none"
              strokeWidth="10"
              strokeDasharray={GAUGE_TRACK}
              transform="rotate(135 60 60)"
            />
            <circle
              className="inspect-landing-gauge-arc"
              cx="60"
              cy="60"
              r="48"
              fill="none"
              strokeWidth="10"
              strokeLinecap="round"
              strokeDasharray={GAUGE_ARC}
              transform="rotate(135 60 60)"
            />
          </svg>
          <span className="inspect-landing-gauge-n" aria-hidden="true">
            <span className="inspect-landing-gauge-score">{t('inspectLanding:gaugeScore')}</span>
            <span className="inspect-landing-gauge-band">{t('inspectLanding:gaugeBand')}</span>
          </span>
        </span>
        <span className="inspect-landing-gauge-tag">
          <span className="sr-only">{t('inspectLanding:gaugeSr')}{' '}</span>
          {t('inspectLanding:gaugeTag')}
        </span>
      </span>
    );
  }

  renderRows(t) {
    var self = this;
    return LEARN_MORE_ROWS.map(function (row) {
      var name = t('inspectLanding:' + row + 'Name');
      return (
        <tr key={row}>
          <th scope="row">{name}</th>
          <td className="inspect-landing-list-what">{t('inspectLanding:' + row + 'What')}</td>
          <td className="inspect-landing-list-ex">
            {row === 'picture' ? (
              self.renderGauge(t)
            ) : (
              <span className="inspect-landing-chip-row">{ROW_EXAMPLES[row](t)}</span>
            )}
          </td>
          <td className="inspect-landing-list-more">
            <button
              type="button"
              className="inspect-landing-link-btn"
              aria-haspopup="dialog"
              aria-label={t('inspectLanding:learnMoreAbout', { feature: name.toLowerCase() })}
              onClick={function () {
                self.openLearnMore(row);
              }}
            >
              {t('inspectLanding:learnMore')}
            </button>
          </td>
        </tr>
      );
    });
  }

  render() {
    var props = this.props;
    var t = props.t;
    var user = props.currentUser || {};
    var stackName = (props.currentAccount && props.currentAccount.name) || '';
    var contactEmail = (user.contactInfo && user.contactInfo.email) || '';
    var contactPhone = (user.contactInfo && user.contactInfo.phone) || '';
    var state = this.state;
    var requestState = state.requestState;
    var sent = !!requestState;
    var note = t('inspectLanding:notOnForStack', { stack: stackName });
    var closeLine = t('inspectLanding:closeLine');
    if (requestState === 'sent') {
      note = t('inspectLanding:sentCaption');
      closeLine = note;
    }
    if (requestState === 'alreadyRequested') {
      note = t('inspectLanding:alreadyCaption');
      closeLine = note;
    }
    var rootClass =
      'inner-page inspect-landing' +
      (state.paused ? ' inspect-landing--paused' : '') +
      (state.openRow !== null || state.dialogOpen ? ' inspect-landing--held' : '');
    var requestButton = (
      <BlueButton
        variant="primary"
        label={t(sent ? 'inspectLanding:requestSent' : 'inspectLanding:requestAccess')}
        disabled={sent}
        onClick={this.openDialog}
      />
    );
    var self = this;

    return (
      <div className={rootClass}>
        <section className="inspect-landing-card inspect-landing-hero">
          {this.renderPause()}
          <div className="inspect-landing-hero-top">
            <div className="inspect-landing-hero-copy">
              <div className="inspect-landing-chips">
                <span className="inspect-landing-chip-new">{t('inspectLanding:chipNew')}</span>
                <span className="inspect-landing-chip-locked">
                  <span className="material-symbols-outlined" aria-hidden="true">lock</span>
                  {t('inspectLanding:chipLocked')}
                </span>
              </div>
              <h2 className="inspect-landing-hero-title">{t('inspectLanding:heroTitle')}</h2>
              <p className="inspect-landing-hero-lede">{t('inspectLanding:heroLede')}</p>
              {requestButton}
              <p className="inspect-landing-note">{note}</p>
            </div>
            <HeroArt t={t} waiting={state.waiting.hero} figureRef={this.setHeroFigure} />
          </div>
          <div className="inspect-landing-video">
            <div className="inspect-landing-video-frame" aria-hidden="true">
              <span className="inspect-landing-ghost" style={{ left: '7%', top: '14%', width: '20%', height: '72%', borderRadius: 10 }} />
              <span className="inspect-landing-ghost" style={{ left: '33%', top: '14%', width: '60%', height: '10%' }} />
              <span className="inspect-landing-ghost" style={{ left: '33%', top: '32%', width: '60%', height: '8%' }} />
              <span className="inspect-landing-ghost" style={{ left: '33%', top: '46%', width: '60%', height: '8%' }} />
              <span className="inspect-landing-ghost" style={{ left: '33%', top: '60%', width: '60%', height: '8%' }} />
              <span className="inspect-landing-play">
                <span className="material-symbols-outlined">play_arrow</span>
              </span>
            </div>
            <div>
              <h3 className="inspect-landing-video-h">{t('inspectLanding:videoTitle')}</h3>
              <p className="inspect-landing-video-p">{t('inspectLanding:videoBody')}</p>
              <p className="inspect-landing-video-tag">{t('inspectLanding:videoTag')}</p>
            </div>
          </div>
        </section>

        <section className="inspect-landing-card inspect-landing-adds">
          <h2 className="inspect-landing-section-title">{t('inspectLanding:listTitle')}</h2>
          <table className="inspect-landing-list">
            <thead className="sr-only">
              <tr>
                <th scope="col">{t('inspectLanding:colFeature')}</th>
                <th scope="col">{t('inspectLanding:colWhat')}</th>
                <th scope="col">{t('inspectLanding:colExample')}</th>
                <th scope="col">{t('inspectLanding:colMore')}</th>
              </tr>
            </thead>
            <tbody>{this.renderRows(t)}</tbody>
          </table>
        </section>

        <section className="inspect-landing-card inspect-landing-crew">
          {this.renderPause()}
          <div className="inspect-landing-crew-in">
            <CrewArt t={t} waiting={state.waiting.crew} figureRef={this.setCrewFigure} />
            <div className="inspect-landing-crew-text">
              <h2 className="inspect-landing-section-title">{t('inspectLanding:crewTitle')}</h2>
              <p className="inspect-landing-crew-p">{t('inspectLanding:crewLine')}</p>
              <ol className={'inspect-landing-steps' + (state.waiting.crew ? ' inspect-landing-wait' : '')}>
                {[1, 2, 3].map(function (n) {
                  return (
                    <li key={n}>
                      <span className="inspect-landing-step-n" aria-hidden="true">
                        {n}
                        <span className={'inspect-landing-step-on inspect-landing-step-lit-' + n}>{n}</span>
                      </span>
                      {t('inspectLanding:crewStep' + n)}
                    </li>
                  );
                })}
              </ol>
              <p className="inspect-landing-crew-p">{t('inspectLanding:crewDone')}</p>
            </div>
          </div>
        </section>

        <div className="inspect-landing-close">
          <div>
            <h2 className="inspect-landing-close-h">{t('inspectLanding:closeTitle', { stack: stackName })}</h2>
            <p className="inspect-landing-close-line">{closeLine}</p>
          </div>
          {requestButton}
        </div>

        {LEARN_MORE_ROWS.map(function (row) {
          return (
            <LearnMoreDialog
              key={row}
              row={row}
              open={state.openRow === row}
              onClose={self.closeLearnMore}
              onRequestAccess={self.requestFromLearnMore}
              requestSent={sent}
              t={t}
            />
          );
        })}
        <RequestAccessDialog
          open={state.dialogOpen}
          onClose={this.closeDialog}
          onSend={props.requestInspectAccess}
          onSent={this.handleSent}
          accountUUID={accountUUIDOf(props)}
          contactEmail={contactEmail}
          contactPhone={contactPhone}
          stackName={stackName}
          t={t}
        />
      </div>
    );
  }
}

InspectLandingPage.propTypes = {
  t: PropTypes.func.isRequired,
  currentUser: PropTypes.object,
  currentAccount: PropTypes.object,
  requestInspectAccess: PropTypes.func.isRequired,
  markInspectLandingSeen: PropTypes.func,
};

function mapStateToProps(state) {
  return {
    currentUser: state.currentUser,
    currentAccount: state.accounts && state.accounts.currentAccount,
  };
}

export default withTranslation()(
  connect(mapStateToProps, { requestInspectAccess, markInspectLandingSeen })(
    InspectLandingPage,
  ),
);
